import React, { useEffect, useRef, useState } from 'react';
import { Calendar, Shield, Zap, Sparkles } from 'lucide-react';

const stats = [
  {
    id: 1,
    number: '5',
    suffix: '+',
    label: 'Years Strong',
    desc: 'Since 2019. Local shoots to international clients across Japan, Thailand and Indonesia.',
    icon: Calendar,
    iconAnim: 'spin-slow',
  },
  {
    id: 2,
    number: '1-5',
    suffix: '',
    label: 'Days Delivery',
    desc: 'Event highlights in 1 to 3 days. Full cinematic edits up to 5 days. No delays.',
    icon: Zap,
    iconAnim: 'pulse-glow',
  },
  {
    id: 3,
    number: '90',
    suffix: '',
    label: 'Days Backup',
    desc: 'Your raw footage stays safe with us for 3 months. Need files later. Just hit us up.',
    icon: Shield,
    iconAnim: 'bounce-subtle',
  },
  {
    id: 4,
    number: '100',
    suffix: '%',
    label: 'Creative Energy',
    desc: 'Want something trending. Flex your ride. Create memories that hit different.',
    icon: Sparkles,
    iconAnim: 'sparkle',
  },
];

export const Benefits = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(null);

  const triggerChange = (newIndex) => {
    setActiveIndex((current) => {
      if (newIndex === current) return current;
      setPrevIndex(current);
      return newIndex;
    });
  };

  // Clear prevIndex after exit animation completes
  useEffect(() => {
    if (prevIndex === null) return;
    const t = setTimeout(() => setPrevIndex(null), 900);
    return () => clearTimeout(t);
  }, [prevIndex, activeIndex]);

  // Intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setIsVisible(true);
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto-cycle every 5s
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      triggerChange((activeIndex + 1) % stats.length);
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, activeIndex]);

  const handleStatClick = (index) => {
    triggerChange(index);
  };

  const ActiveIcon = stats[activeIndex].icon;

  return (
    <section 
      ref={sectionRef}
      className="py-20 md:py-28 relative overflow-hidden"
      style={{ 
        background: 'linear-gradient(135deg, #0a0400 0%, #1a0800 50%, #0a0400 100%)'
      }}
      data-testid="why-us-section"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Floating orbs */}
        <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-orange-500/5 blur-3xl animate-float-slow" />
        <div className="absolute bottom-20 right-10 w-40 h-40 rounded-full bg-amber-500/5 blur-3xl animate-float-slow-reverse" />
        <div className="absolute top-1/2 left-1/3 w-24 h-24 rounded-full bg-orange-400/5 blur-2xl animate-pulse-slow" />
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 relative z-10">
        
        {/* Header with visual elements */}
        <div className="text-center mb-12 md:mb-16 relative">
          {/* Decorative lines */}
          <div className="absolute left-1/2 -translate-x-1/2 top-0 w-px h-8 bg-gradient-to-b from-transparent via-orange-500/50 to-transparent hidden md:block" />
          
          {/* Animated icon above title */}
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500/20 to-amber-500/20 flex items-center justify-center animate-bounce-subtle">
                <Sparkles className="w-6 h-6 text-orange-400 animate-pulse" />
              </div>
              {/* Glow ring */}
              <div className="absolute inset-0 rounded-xl bg-orange-500/20 blur-xl animate-pulse-slow" />
            </div>
          </div>

          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 relative inline-block">
            <span className="text-white">Why </span>
            <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-orange-500 bg-clip-text text-transparent">Us</span>
            {/* Underline decoration */}
            <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-orange-500 to-transparent" />
          </h2>
          
          <p className="text-gray-400 text-base md:text-lg max-w-lg mx-auto mt-6">
            Not your average production house. We deliver results.
          </p>
          
          {/* Decorative dots */}
          <div className="flex justify-center gap-2 mt-4">
            <div className="w-1.5 h-1.5 rounded-full bg-orange-500/40 animate-pulse" />
            <div className="w-1.5 h-1.5 rounded-full bg-orange-500/60 animate-pulse delay-100" />
            <div className="w-1.5 h-1.5 rounded-full bg-orange-500/40 animate-pulse delay-200" />
          </div>
        </div>

        {/* Stats Display - Desktop */}
        <div className="hidden md:grid md:grid-cols-2 gap-8 md:gap-16 items-center">
          
          {/* Left: Number — vertical scrolling odometer reel */}
          <div className="text-center md:text-left relative">
            {/* Soft icon backdrop */}
            <div className="absolute -top-2 -left-2 opacity-[0.08] pointer-events-none">
              <ActiveIcon className="w-20 h-20 text-orange-500" />
            </div>

            {/* Vertical scrolling number reel */}
            <div
              className="relative overflow-hidden number-reel-mask"
              style={{ height: 'clamp(5rem, 9vw, 8rem)' }}
            >
              <div
                className="flex flex-col will-change-transform"
                style={{
                  transform: `translateY(-${activeIndex * (100 / stats.length)}%)`,
                  transition: 'transform 0.85s cubic-bezier(0.65, 0, 0.35, 1)',
                }}
              >
                {stats.map((stat) => (
                  <span
                    key={stat.id}
                    className="font-black leading-none flex items-center md:justify-start justify-center shrink-0"
                    style={{
                      height: 'clamp(5rem, 9vw, 8rem)',
                      fontSize: 'clamp(4.5rem, 9vw, 8rem)',
                      background: 'linear-gradient(135deg, #f97316 0%, #fbbf24 50%, #f97316 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      textShadow: '0 0 60px rgba(249, 115, 22, 0.2)',
                    }}
                  >
                    {stat.number}{stat.suffix}
                  </span>
                ))}
              </div>
            </div>

            {/* Label — vertical scrolling reel (matches numbers) */}
            <div className="relative overflow-hidden h-9 mt-3">
              <div
                className="flex flex-col will-change-transform"
                style={{
                  transform: `translateY(-${activeIndex * (100 / stats.length)}%)`,
                  transition: 'transform 0.85s cubic-bezier(0.65, 0, 0.35, 1)',
                }}
              >
                {stats.map((stat) => (
                  <p
                    key={stat.id}
                    className="h-9 flex items-center md:justify-start justify-center text-lg md:text-xl font-semibold text-white shrink-0"
                  >
                    {stat.label}
                  </p>
                ))}
              </div>
            </div>

            {/* Progress Dots */}
            <div className="flex justify-center md:justify-start gap-3 mt-6">
              {stats.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleStatClick(index)}
                  data-testid={`why-us-dot-${index}`}
                  className={`h-2 rounded-full transition-all duration-500 ${
                    index === activeIndex 
                      ? 'w-10 bg-gradient-to-r from-orange-400 to-amber-500' 
                      : 'w-2 bg-white/20 hover:bg-white/40'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Right: Description — exit slide-left + blur + zoom-out, enter slide-in-from-right + blur */}
          <div className="relative h-[220px] perspective-1000">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              const isActive = index === activeIndex;
              const isExiting = index === prevIndex;
              const isHidden = !isActive && !isExiting;

              let stateClass = 'box-hidden';
              if (isActive) stateClass = 'box-enter';
              else if (isExiting) stateClass = 'box-exit';

              return (
                <div
                  key={stat.id}
                  className={`absolute inset-0 ${stateClass}`}
                  style={{ pointerEvents: isActive ? 'auto' : 'none' }}
                >
                  <div 
                    className="h-full p-6 md:p-8 rounded-2xl border border-orange-500/20 relative overflow-hidden"
                    style={{
                      background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.08) 0%, rgba(0,0,0,0.5) 100%)',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    {/* Icon backdrop */}
                    <div className="absolute top-4 right-4 opacity-10">
                      <Icon className="w-20 h-20 text-orange-500" />
                    </div>

                    {/* Badge */}
                    <div className="inline-flex items-center gap-2 mb-4 relative z-10">
                      <span className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-orange-400" />
                      </span>
                      <span className="text-orange-400/80 text-sm uppercase tracking-wider font-medium">
                        {stat.label}
                      </span>
                    </div>

                    {/* Description */}
                    <p className="text-gray-300 text-base md:text-lg leading-relaxed relative z-10">
                      {stat.desc}
                    </p>

                    {/* Accent line */}
                    <div className="w-12 h-1 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full mt-6" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile: Enhanced Vertical List */}
        <div className="md:hidden space-y-4">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            const isActive = index === activeIndex;
            
            return (
              <div
                key={`mobile-${stat.id}`}
                className={`p-5 rounded-xl border transition-all duration-500 ${
                  isActive 
                    ? 'border-orange-500/40 bg-gradient-to-r from-orange-500/10 to-transparent scale-[1.02]' 
                    : 'border-white/5 bg-white/[0.02]'
                }`}
                onClick={() => handleStatClick(index)}
              >
                <div className="flex items-center gap-4">
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500/20 to-amber-500/10 flex items-center justify-center ${isActive ? 'animate-pulse' : ''}`}>
                    <Icon className={`w-7 h-7 text-orange-400 ${isActive ? `icon-${stat.iconAnim}` : ''}`} />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span 
                        className="text-2xl font-black"
                        style={{
                          background: 'linear-gradient(135deg, #f97316 0%, #fbbf24 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        {stat.number}{stat.suffix}
                      </span>
                      <span className="text-white font-semibold text-sm">
                        {stat.label}
                      </span>
                    </div>
                    <p className="text-gray-400 text-xs mt-1 line-clamp-2">
                      {stat.desc}
                    </p>
                  </div>
                  
                  {/* Active indicator */}
                  {isActive && (
                    <div className="w-1 h-10 rounded-full bg-gradient-to-b from-orange-500 to-amber-500" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Custom animations */}
      <style>{`
        /* Top/bottom soft fade on number reel for cinematic feel */
        .number-reel-mask {
          -webkit-mask-image: linear-gradient(to bottom, transparent 0%, black 18%, black 82%, transparent 100%);
          mask-image: linear-gradient(to bottom, transparent 0%, black 18%, black 82%, transparent 100%);
        }

        /* Right-side description box transitions */
        .perspective-1000 {
          perspective: 1000px;
        }
        .box-hidden {
          opacity: 0;
          pointer-events: none;
        }
        .box-enter {
          animation: boxSlideInRight 0.85s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .box-exit {
          animation: boxSlideOutLeft 0.85s cubic-bezier(0.5, 0, 0.75, 0) forwards;
        }
        @keyframes boxSlideInRight {
          0% {
            opacity: 0;
            transform: translateX(80%) scale(0.92);
            filter: blur(12px);
          }
          60% {
            opacity: 1;
            filter: blur(2px);
          }
          100% {
            opacity: 1;
            transform: translateX(0) scale(1);
            filter: blur(0);
          }
        }
        @keyframes boxSlideOutLeft {
          0% {
            opacity: 1;
            transform: translateX(0) scale(1);
            filter: blur(0);
          }
          40% {
            opacity: 0.85;
            filter: blur(6px);
          }
          100% {
            opacity: 0;
            transform: translateX(-80%) scale(0.7);
            filter: blur(14px);
          }
        }

        @keyframes float-slow {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-20px) translateX(10px); }
        }
        @keyframes float-slow-reverse {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(20px) translateX(-10px); }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.1); }
        }
        @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .animate-float-slow { animation: float-slow 8s ease-in-out infinite; }
        .animate-float-slow-reverse { animation: float-slow-reverse 10s ease-in-out infinite; }
        .animate-pulse-slow { animation: pulse-slow 4s ease-in-out infinite; }
        .animate-bounce-subtle { animation: bounce-subtle 2s ease-in-out infinite; }
        
        .icon-spin-slow { animation: spin 8s linear infinite; }
        .icon-pulse-glow { animation: pulse-slow 2s ease-in-out infinite; }
        .icon-bounce-subtle { animation: bounce-subtle 1.5s ease-in-out infinite; }
        .icon-sparkle { animation: sparkle 2s ease-in-out infinite; }
        
        @keyframes sparkle {
          0%, 100% { opacity: 1; transform: scale(1) rotate(0deg); }
          50% { opacity: 0.7; transform: scale(1.1) rotate(10deg); }
        }
        
        .delay-100 { animation-delay: 100ms; }
        .delay-200 { animation-delay: 200ms; }
      `}</style>
    </section>
  );
};
